import EmptyFolder from 'assets/img/app/empty-folder.png';
import { IonIcon, IonSpinner } from "@ionic/react";
import React, { SetStateAction, useEffect, useState } from "react";
import { calculateSellerFulfillmentPercentage, formatDateToISODateString, getSearchFiltersValues } from "utils";
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, Pagination } from '@mui/material';
import { caretDown } from 'ionicons/icons';
import { SearchFilterItem, SearchFilters, SellerPerformanceRecord } from 'interfaces';
import { IconSeller } from 'assets';
import { getSellersControlService, getRoutesSellerService } from 'services';
import { useAppSelector } from 'hooks';

// Color barra
const getBarColor = (percentage: number) => {
  if (percentage <= 30) return 'bg-progress-bar-red';
  if (percentage <= 50) return 'bg-progress-bar-orange';
  if (percentage <= 80) return 'bg-progress-bar-yellow';
  return 'bg-progress-bar-green';
};

type CustomerVisitControlFilters = "vendorName";

const initialSearchFilters: Record<CustomerVisitControlFilters, SearchFilters> = {
  vendorName: {
    allChecked: true,
    allHidden: false,
    items: [],
  },
};

export const CustomersVisitControl = (
  {
    showOrdersDetails,
    contentRef,
    today,
    startDate,
    setStartDate,
    startDateSearchFilter,
    setStartDateSearchFilter,
    finishDate,
    setFinishDate,
    finishDateSearchFilter,
    setFinishDateSearchFilter,
    hasDatesChanged,
    setHasDatesChanged,
    selectedVendors,
    onChangeSelectedVendors,
  }:
    {
      showOrdersDetails: boolean,
      contentRef: React.RefObject<HTMLIonContentElement>,
      today: Date,
      startDate: Date,
      setStartDate: React.Dispatch<SetStateAction<Date>>,
      startDateSearchFilter: Date,
      setStartDateSearchFilter: React.Dispatch<SetStateAction<Date>>,
      finishDate: Date,
      setFinishDate: React.Dispatch<SetStateAction<Date>>,
      finishDateSearchFilter: Date,
      setFinishDateSearchFilter: React.Dispatch<SetStateAction<Date>>,
      hasDatesChanged: number,
      setHasDatesChanged: React.Dispatch<SetStateAction<number>>,
      selectedVendors: string[] | null,
      onChangeSelectedVendors: (v: string[] | null) => void,
    }
) => {
  const { token } = useAppSelector(state => state.user);

  // Data
  const [sellersData, setSellersData] = useState<any[]>([]);
  const [filteredSellersData, setFilteredSellersData] = useState<any[]>([]);

  // Reporte (usamos visibleReport como fuente de verdad para la tabla)
  const [report, setReport] = useState<SellerPerformanceRecord[][]>([]);
  const [visibleReport, setVisibleReport] = useState<SellerPerformanceRecord[]>([]);

  // Paginación
  const [paginationCount, setPaginationCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [recordsPerView] = useState<number>(10);

  // Loading flags
  const [searchingSellersData, setSearchingSellersData] = useState<boolean>(false);
  const [filteringReport, setFilteringReport] = useState<boolean>(false);
  const [routesData, setRoutesData] = useState<any[]>([]);

  // Filtros vendedores
  const [searchFilters, setSearchFilters] = useState<Record<CustomerVisitControlFilters, SearchFilters>>(initialSearchFilters);

  // --- helper para construir una fila a partir de un "seller"
  const buildRow = (seller: any): SellerPerformanceRecord => {
    const assignedCustomers =
      routesData && routesData.length > 0
        ? (() => {
          const foundRoute = routesData.find(
            (route: any) => String(route.Ruta) === String(seller.router)
          );
          return foundRoute ? Number(foundRoute.TotalClientes) || 0 : 0;
        })()
        : Number(seller.allsClients) || 0;

    return {
      seller: seller.vendorName,
      code: seller.salesPersonCode,
      assignedCustomers,
      visitedCustomers: seller.visitClient,
      percentage: calculateSellerFulfillmentPercentage(
        assignedCustomers,
        seller.visitClient
      ),
    };
  };

  const getSellersData = async () => {
    const startKey = startDate.getTime();
    const finishKey = finishDate.getTime();

    try {
      setSearchingSellersData(true);
      if (!token) throw new Error("No token");

      let sellersReport = await getSellersControlService(
        token,
        formatDateToISODateString(startDate),   // commit start (00:00)
        formatDateToISODateString(finishDate)   // commit end (EOD ya aplicado en padre)
      );

      if (startKey !== startDate.getTime() || finishKey !== finishDate.getTime()) return;

      // 1 registro por vendedor
      sellersReport = sellersReport.filter(
        (item: any, idx: number, self: any) =>
          idx === self.findIndex((t: any) => t.vendorName === item.vendorName)
      );

      const routesDataResp = await getRoutesSellerService(
        token,
        formatDateToISODateString(startDate),
        formatDateToISODateString(finishDate)
      );

      if (startKey !== startDate.getTime() || finishKey !== finishDate.getTime()) return;

      setRoutesData(routesDataResp);

      // Checkboxes de vendedores
      const vendorNames: string[] = Array.from(
        new Set(sellersReport.map((x: any) => x.vendorName).filter(Boolean))
      );

      const items = vendorNames.map(v => ({
        value: v,
        label: v,
        visible: true,
        checked: selectedVendors ? selectedVendors.includes(v) : true, // null => todos
      }));

      setSearchFilters({
        vendorName: {
          allChecked: items.length > 0 && items.every(i => i.checked),
          allHidden: items.length === 0,
          items,
        },
      });

      const baseData = sellersReport;
      const filtered =
        selectedVendors === null
          ? baseData
          : baseData.filter((row: any) => selectedVendors.includes(row.vendorName));

      setSellersData(baseData);
      setFilteredSellersData(filtered);

      setPaginationCount(Math.ceil(filtered.length / recordsPerView));
      setCurrentPage(1);

      // Primera página visible (usando el snapshot local "routesDataResp")
      const firstPage = filtered.slice(0, recordsPerView).map((seller: any) => {
        const assignedCustomers =
          routesDataResp && routesDataResp.length > 0
            ? (() => {
              const foundRoute = routesDataResp.find(
                (route: any) => String(route.Ruta) === String(seller.router)
              );
              return foundRoute ? Number(foundRoute.TotalClientes) || 0 : 0;
            })()
            : Number(seller.allsClients) || 0;

        return {
          seller: seller.vendorName,
          code: seller.salesPersonCode,
          assignedCustomers,
          visitedCustomers: seller.visitClient,
          percentage: calculateSellerFulfillmentPercentage(
            assignedCustomers,
            seller.visitClient
          ),
        };
      });

      const reportAux: SellerPerformanceRecord[][] = [firstPage];
      setReport(reportAux);
      setVisibleReport(reportAux[0] ?? []);
    } catch (error) {
      setSellersData([]);
      setFilteredSellersData([]);
      setSearchFilters({
        vendorName: {
          allChecked: true,
          allHidden: true,
          items: [],
        },
      });
      setReport([]);
      setVisibleReport([]);
      setPaginationCount(0);
      setCurrentPage(1);
    } finally {
      setSearchingSellersData(false);
    }
  };

  // 🔁 Refetch cuando cambia el "commit" (hasDatesChanged)
  useEffect(() => {
    getSellersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDatesChanged]);

  // ✅ Recalcular tabla y paginación cuando cambie el filtrado/rutas
  useEffect(() => {
    setCurrentPage(1);
    setPaginationCount(Math.ceil(filteredSellersData.length / recordsPerView));

    const first = filteredSellersData.slice(0, recordsPerView).map(buildRow);
    setVisibleReport(first);
  }, [filteredSellersData, routesData, recordsPerView]);

  // Paginar tomando el slice de filteredSellersData
  const changePaginationHandler = (_event: React.ChangeEvent<unknown>, value: number) => {
    setFilteringReport(true);

    const start = (value - 1) * recordsPerView;
    const page = filteredSellersData.slice(start, start + recordsPerView).map(buildRow);

    setVisibleReport(page);
    setCurrentPage(value);

    if (contentRef && contentRef.current) {
      contentRef.current.scrollToTop(500);
    }

    setFilteringReport(false);
  };

  // --------- Filtros de vendedores
  useEffect(() => {
    const vendorNames = Array.from(
      new Set(sellersData.map((x: any) => x.vendorName).filter(Boolean))
    );

    const items = vendorNames.map(v => ({
      value: v,
      label: v,
      visible: true,
      checked: selectedVendors ? selectedVendors.includes(v) : true, // null => todos
    }));

    setSearchFilters({
      vendorName: {
        allChecked: items.length > 0 && items.every(i => i.checked),
        allHidden: items.length === 0,
        items,
      },
    });

    const filtered =
      selectedVendors === null
        ? sellersData
        : sellersData.filter((row: any) => selectedVendors.includes(row.vendorName));

    setFilteredSellersData(filtered);
    setCurrentPage(1);
    setPaginationCount(Math.ceil(filtered.length / recordsPerView));
  }, [selectedVendors, sellersData, recordsPerView]);

  function emitSelectedVendors(sf: Record<CustomerVisitControlFilters, SearchFilters>) {
    const visible = sf.vendorName.items.filter(i => i.visible);
    const checked = visible.filter(i => i.checked).map(i => i.value);
    onChangeSelectedVendors(checked.length === visible.length ? null : checked);
  }

  const allCheckedChangeHandler = () => {
    const isAllChecked = searchFilters.vendorName.allChecked;
    if (isAllChecked) onChangeSelectedVendors([]);
    else onChangeSelectedVendors(null);
  };

  const changeSearchFilterHandler = (event: React.ChangeEvent<HTMLInputElement>, filterName: CustomerVisitControlFilters) => {
    const filterCategory = searchFilters[filterName];
    const filterArray = filterCategory.items;

    const indexFilter = filterArray.findIndex(item => item.value === event.target.value);
    if (indexFilter === -1) return;

    const updatedFilter = { ...filterArray[indexFilter], checked: event.target.checked };

    const updatedFilterArray = [
      ...filterArray.slice(0, indexFilter),
      updatedFilter,
      ...filterArray.slice(indexFilter + 1)
    ];

    const searchFiltersAux = {
      ...searchFilters,
      [filterName]: {
        ...searchFilters[filterName],
        items: updatedFilterArray
      }
    };

    updateFiltersAndBenchmarks(searchFiltersAux, filterName);
    emitSelectedVendors(searchFiltersAux);
  };

  const updateFiltersAndBenchmarks = (searchFiltersAux: Record<CustomerVisitControlFilters, SearchFilters>, filterName: CustomerVisitControlFilters) => {
    const filterCategory = searchFiltersAux[filterName];
    const checkedFilters = filterCategory.items.filter(item => item.checked).map(item => item.value);
    let newFilteredSellersData = sellersData.filter(item => checkedFilters.includes(item[filterName]));

    for (const key in searchFiltersAux) {
      if (key !== filterName) {
        const foundSearchFilters = getSearchFiltersValues(newFilteredSellersData, key as CustomerVisitControlFilters);

        searchFiltersAux[key as CustomerVisitControlFilters].items = searchFiltersAux[key as CustomerVisitControlFilters].items.map(item => ({
          ...item,
          visible: foundSearchFilters.includes(item.value)
        }));
      }

      const allChecked = searchFiltersAux[key as CustomerVisitControlFilters].items.every(item => !item.visible || item.checked);
      const allHidden = searchFiltersAux[key as CustomerVisitControlFilters].items.every(item => !item.visible);

      searchFiltersAux[key as CustomerVisitControlFilters] = {
        ...searchFiltersAux[key as CustomerVisitControlFilters],
        allChecked,
        allHidden,
      };
    }

    const activeFilters = Object.keys(searchFiltersAux).reduce((acc, key) => {
      const filters = searchFiltersAux[key as CustomerVisitControlFilters].items
        .filter(item => item.checked && item.visible)
        .map(item => item.value);
      return { ...acc, [key]: filters };
    }, {} as Record<CustomerVisitControlFilters, string[]>);

    newFilteredSellersData = sellersData.filter(item => {
      return Object.keys(activeFilters).every(key => {
        const filterKey = key as CustomerVisitControlFilters;
        return activeFilters[filterKey].includes(item[filterKey]);
      });
    });

    setSearchFilters(searchFiltersAux);
    setFilteredSellersData(newFilteredSellersData);
    setCurrentPage(1);
    setPaginationCount(Math.ceil(newFilteredSellersData.length / recordsPerView));
  };

  // Total de clientes visitados en la PAGINA ACTUAL (visibleReport)
  const totalVisitedOnPage = visibleReport.reduce(
    (sum, row) => sum + (Number(row.visitedCustomers) || 0),
    0
  );

  const totalAssignedOnPage = visibleReport.reduce(
    (sum, row) => sum + (Number(row.assignedCustomers) || 0),
    0
  );

  // Calcular porcentaje de efectividad general
const generalEffectivenessPercentage = totalAssignedOnPage > 0
  ? Math.round((totalVisitedOnPage * 100) / totalAssignedOnPage)
  : 0;


  return (
    <>
      <div id="main-section" className={`w-full h-screen overflow-y-scroll grid grid-cols-10 ${showOrdersDetails && 'hidden'}`}>
        {searchingSellersData && (
          <div className="col-span-10 w-full h-full flex flex-col items-center justify-center">
            <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
          </div>
        )}

        {!searchingSellersData && (
          <div className="w-full flex flex-col flex-grow col-span-7">
            {filteringReport && (
              <div className="w-full h-screen flex flex-col items-center justify-center">
                <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
              </div>
            )}

            {!filteringReport && !(visibleReport.length > 0) && (
              <div className="h-screen grid grid-cols-8">
                <div className="col-span-8 flex flex-col justify-center items-center gap-4 p-4">
                  <img src={EmptyFolder} alt='empty' className="h-[200px] object-contain" />
                  <p className="text-xl text-center font-semibold text-primary">No se encontraron resultados para tu búsqueda</p>
                </div>
              </div>
            )}

            {!filteringReport && visibleReport.length > 0 && (
              <div className="w-full h-full flex flex-col">
                <div className="px-2 pt-4 pb-1 grid grid-cols-5  gap-4 text-mid border-b-2 border-tertiary [&>p]:mt-auto [&>p]:text-primary [&>p]:font-semibold">
                  <p>Vendedor</p>
                  <p>Código de Vendedor</p>
                  <p>Clientes Asignados</p>
                  <p>Clientes Visitados</p>
                  <p>Porcentaje de Efectividad</p>
                </div>

                <div className="w-full flex flex-col">
                  {visibleReport.map((item: SellerPerformanceRecord) => (
                    <div key={item.code} className='w-full px-2 py-1 bg-white odd:bg-light cursor-pointer [&>div]:text-ellipsis [&>div]:overflow-hidden [&>div]:... [&>div]:p-2 text-sm text-gray-700 grid grid-cols-5'>
                      <div className="w-full flex items-center gap-3">
                        <div className="w-[18px]">
                          <IconSeller />
                        </div>
                        <p>{item.seller}</p>
                      </div>
                      <div>{item.code}</div>
                      <div>{item.assignedCustomers}</div>
                      <div>{item.visitedCustomers}</div>
                      <div className="w-full flex items-center gap-4">
                        <div className="progress-bar-container w-full h-[6px] bg-progress-bar-gray rounded-md overflow-hidden">
                          <div className={`progress-bar h-[6px] ${getBarColor(item.percentage)}`} style={{ width: `${item.percentage}%` }} />
                        </div>
                        <p>{item.percentage}%</p>
                      </div>
                    </div>
                  ))}
                  {/* Fila de totales (página actual) */}
                  <div className="w-full px-2 py-2 bg-white border-t-2 border-tertiary font-bold text-lg text-primary grid grid-cols-5">
                    <div>Totales</div>
                    <div></div> {/* Código de Vendedor */}
                    <div>{totalAssignedOnPage}</div>   {/* Clientes Asignados */}
                    <div>{totalVisitedOnPage}</div>    {/* Clientes Visitados */}
                    <div className="w-full flex items-center gap-4">
                      <div className="progress-bar-container w-full h-[6px] bg-progress-bar-gray rounded-md overflow-hidden">
                        <div className={`progress-bar h-[6px] ${getBarColor(generalEffectivenessPercentage)}`} style={{ width: `${generalEffectivenessPercentage}%` }} />
                      </div>
                      <p>{generalEffectivenessPercentage}%</p>
                    </div>
                  </div>

                </div>
              </div>
            )}

            <div className="w-full flex justify-center mt-auto py-4">
              <Pagination
                size="large"
                color="primary"
                count={paginationCount}
                defaultPage={1}
                page={currentPage}
                siblingCount={1}
                boundaryCount={2}
                onChange={changePaginationHandler}
              />
            </div>
          </div>
        )}

        {/* Filtros: sólo Vendedores (quitamos Fecha) */}
        {!searchingSellersData && (
          <div className="w-full overflow-y-scroll flex p-2 flex-col col-span-3 order-last border-l-2 border-light">
            <Accordion className="accordion-search-filter">
              <AccordionSummary
                expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <p className="font-semibold text-mid">Vendedores</p>
              </AccordionSummary>

              <AccordionDetails>
                <div className="flex flex-col">
                  {searchFilters.vendorName.allHidden && (
                    <p className="font-semibold text-primary">No hay datos disponibles</p>
                  )}

                  {!searchFilters.vendorName.allHidden && (
                    <FormControlLabel
                      label={"Todos"}
                      control={
                        <Checkbox
                          id={"displays_all"}
                          name={"displays_all"}
                          checked={searchFilters.vendorName.allChecked}
                          onChange={allCheckedChangeHandler}
                        />
                      }
                    />
                  )}

                  {!searchFilters.vendorName.allHidden && searchFilters.vendorName.items.map((item: SearchFilterItem) => {
                    if (!item.visible) return null;
                    return (
                      <FormControlLabel
                        key={item.value}
                        label={item.label}
                        control={
                          <Checkbox
                            id={item.value}
                            name={item.value}
                            value={item.value}
                            checked={item.checked}
                            onChange={(event) => changeSearchFilterHandler(event, "vendorName")}
                          />
                        }
                      />
                    );
                  })}
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        )}
      </div>
    </>
  );
};
